import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { createBlogPost } from "../services/blogService";
import { apiRequest } from "../services/api";

export default function useBlogDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = id === "new";
  const isViewMode = !isNew && searchParams.get("mode") === "view";

  // ── Form field state ───────────────────────────────────────────────────────
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [category, setCategory] = useState("1");
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [status, setStatus] = useState("published");
  const [slug, setSlug] = useState("");
  const [seoTitle, setSeoTitle] = useState("");
  const [seoDescription, setSeoDescription] = useState("");
  const [author, setAuthor] = useState("");
  const [categories, setCategories] = useState([]);

  // ── API state ──────────────────────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(false);   // true while saving
  const [isFetching, setIsFetching] = useState(false); // true while loading post for edit
  const [error, setError] = useState(null);             // error message string or null
  const [savedPostId, setSavedPostId] = useState(null); // ID of the post after first save

  // ── Load existing post for view/edit mode ─────────────────────────────────
  useEffect(() => {
    if (isNew) return;

    let active = true;
    setIsFetching(true);
    setError(null);

    apiRequest(`/admin/blog/posts/${id}`)
      .then((json) => {
        if (!active) return;
        const post = json.data;
        setTitle(post.title ?? "");
        setContent(post.body ?? "");
        setStatus(post.status ?? "published");
        setAuthor(post.author_name ?? "");
        setImageUrl(post.cover_image_url ?? "");
        const catVal = post.category;
        setCategory(
          typeof catVal === "object" && catVal !== null
            ? String(catVal.id || catVal.categoryName || "1")
            : (catVal ? String(catVal) : "1")
        );
        setTags(Array.isArray(post.tags) ? post.tags : []);
        setSlug(post.slug ?? "");
        setSeoTitle(post.meta_title ?? "");
        setSeoDescription(post.meta_description ?? "");
        setSavedPostId(post.id);
      })
      .catch((err) => {
        if (!active) return;
        setError(err.message || "Failed to load blog post.");
      })
      .finally(() => {
        if (active) setIsFetching(false);
      });

    return () => { active = false; };
  }, [id, isNew]);

  // ── Fetch all categories ──────────────────────────────────────────────────
  useEffect(() => {
    let active = true;
    apiRequest("/categories")
      .then((json) => {
        if (!active) return;
        if (json.status && Array.isArray(json.data)) {
          setCategories(json.data);
          // Convert category name to ID if it exists in the fetched categories list
          setCategory((prevCategory) => {
            const found = json.data.find(
              (cat) =>
                cat.categoryName.toLowerCase() === String(prevCategory).toLowerCase() ||
                String(cat.id) === String(prevCategory)
            );
            return found ? String(found.id) : prevCategory;
          });
        }
      })
      .catch((err) => {
        console.error("Failed to fetch categories:", err);
      });
    return () => { active = false; };
  }, []);

  // ── Auto-generate slug from title for new posts ────────────────────────────
  // Only runs when creating a new post (isNew === true)
  useEffect(() => {
    if (!isNew) return; // don't overwrite loaded slug on edit
    const generatedSlug = title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)+/g, "");
    setSlug(generatedSlug);
  }, [title, isNew]);

  // ── Derived / computed state ───────────────────────────────────────────────
  const wordCount = useMemo(() => {
    const trimmed = content.trim();
    return trimmed ? trimmed.split(/\s+/).length : 0;
  }, [content]);

  const charCount = useMemo(() => content.length, [content]);

  const computedSeoTitle = useMemo(() => {
    if (seoTitle) return seoTitle;
    return `${title || "Untitled Post"} - Zytama Editorial`;
  }, [seoTitle, title]);

  const computedSeoDescription = useMemo(() => {
    if (seoDescription) return seoDescription;
    if (content) {
      return content.length > 150 ? content.substring(0, 150) + "..." : content;
    }
    return "Start writing your meta description here to see how your post will appear in search engine results...";
  }, [seoDescription, content]);

  // ── Tag handlers ───────────────────────────────────────────────────────────
  const handleAddTag = (e) => {
    if (e && e.key && e.key !== "Enter") return;
    if (e) e.preventDefault();
    const trimmed = tagInput.trim().toLowerCase();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  // ── Save handler — calls the real API ─────────────────────────────────────
  const handleSave = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const payload = {
      title,
      body: content,
      cover_image_url: imageUrl || null,
      category: String(category),
      tags,
      status,
      slug: slug || null,           // null → backend auto-generates from title
      author_name: author || null,
      meta_title: seoTitle || null,
      meta_description: seoDescription || null,
    };

    if (!isNew) {
      payload.id = id;
    }

    try {
      const savedPost = await createBlogPost(payload);
      if (isNew) {
        setSavedPostId(savedPost.id);
      }

      // Navigate back to the blog list after successful save
      navigate("/blogmanage");
    } catch (err) {
      setError(err.message || "Failed to save the blog post. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [
    title, content, imageUrl, category, tags, status,
    slug, author, seoTitle, seoDescription,
    isNew, id, navigate,
  ]);

  const handleCancel = () => {
    navigate("/blogmanage");
  };

  // ── Toggle publish/unpublish status ───────────────────────────────────────
  const handleToggleStatus = useCallback(async () => {
    if (!id || id === "new") return;
    setIsLoading(true);
    setError(null);
    try {
      const json = await apiRequest(`/admin/blog/posts/${id}/toggle-status`, {
        method: "PUT",
      });
      if (json.success && json.data?.status) {
        setStatus(json.data.status);
      }
    } catch (err) {
      setError(err.message || "Failed to toggle blog status. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  // ── Expose to component ────────────────────────────────────────────────────
  return {
    // Meta
    isNew,
    isViewMode,
    isLoading,
    isFetching,
    error,

    // Form fields
    title,        setTitle,
    content,      setContent,
    status,       setStatus,
    slug,         setSlug,
    imageUrl,     setImageUrl,
    category,     setCategory,
    categories,
    tags,
    tagInput,     setTagInput,
    seoTitle,     setSeoTitle,
    seoDescription, setSeoDescription,
    author,       setAuthor,

    // Derived
    computedSeoTitle,
    computedSeoDescription,
    wordCount,
    charCount,

    // Handlers
    handleAddTag,
    handleRemoveTag,
    handleSave,
    handleCancel,
    handleToggleStatus,
  };
}
